import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Globe, LayoutTemplate, Mail, Folder, Database, ArrowLeft, ExternalLink, LifeBuoy, Activity,
  Play, Square, RotateCw, ShieldCheck, Archive, Loader2, Plus, Paperclip, Send, MessageSquare,
} from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { ticketCategories, ticketPriorities } from "@/mocks/portal";
import { TintiLogo } from "@/components/layout/TintiLogo";
import { PortalProjects } from "@/components/portal/PortalProjects";
import { StatusBadge, DemoBadge, AsyncBoundary, CardSkeleton, UserAvatar, EmptyState, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { demoAction } from "@/lib/demo";
import { http } from "@/lib/apiClient";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const SERVICE_ICONS = { layout: LayoutTemplate, mail: Mail, archive: Archive, folder: Folder, database: Database, globe: Globe };
const PRIORITY = { high: "text-err", medium: "text-warn", low: "text-muted-foreground" };
const PRIORITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };
const TRANSIENT = ["starting", "restarting", "stopping"];

/* ---------- Meu Site (gerenciamento com start/stop/restart) ---------- */
const SiteCard = ({ site }) => {
  const [status, setStatus] = useState(site.status);
  const [confirm, setConfirm] = useState(null); // 'restart' | 'stop'
  const busy = TRANSIENT.includes(status);
  const perms = site.permissions;

  useEffect(() => setStatus(site.status), [site.status]);
  const run = async (action) => {
    try {
      const result = await http.post(`/sites/${site.id}/${action}`);
      setStatus(result.status);
      toast('Operação enviada. Aguardando confirmação do servidor.');
    } catch(e) {toast.error(e.message);}
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface" data-testid="portal-site-card">
      <div className="flex flex-col gap-4 border-b border-line p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Globe className="h-6 w-6" /></div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-heading text-lg font-semibold text-foreground">{site.name}</h2>
              <StatusBadge status={status} size="sm" pulse={!busy} />
            </div>
            <p className="font-mono text-sm text-muted-foreground">{site.domain}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" disabled={!site.domain} onClick={() => window.open(`https://${site.domain}`, "_blank", "noopener,noreferrer")} className="border-line bg-transparent hover:bg-white/5" data-testid="site-open"><ExternalLink className="h-4 w-4" /> Abrir Site</Button>
          {status === "offline" || status === "error" ? (
            <Button disabled={busy || !perms.canStart} onClick={() => run("start")} className="bg-ok text-white hover:bg-ok/90" data-testid="site-start">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Iniciar
            </Button>
          ) : (
            <>
              <Button variant="outline" disabled={busy || !perms.canRestart} onClick={() => setConfirm("restart")} className="border-warn/30 bg-transparent text-warn hover:bg-warn/10" data-testid="site-restart">
                {status === "restarting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} Reiniciar
              </Button>
              <Button variant="outline" disabled={busy || !perms.canStop} onClick={() => setConfirm("stop")} className="border-err/30 bg-transparent text-err hover:bg-err/10" data-testid="site-stop">
                {status === "stopping" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />} Parar
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px bg-line sm:grid-cols-4">
        {[
          { label: "SSL", value: "Não verificado", icon: ShieldCheck },
          { label: "Disponibilidade", value: site.uptime, icon: Activity },
          { label: "Último backup", value: site.lastBackup, icon: Archive },
          { label: "Plano", value: site.plan },
        ].map((m) => (
          <div key={m.label} className="bg-surface p-4">
            <p className="text-xs text-muted-foreground">{m.label}</p>
            <p className={cn("mt-0.5 font-heading text-base font-bold", m.tone || "text-foreground")}>{m.value}</p>
          </div>
        ))}
      </div>
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === "stop" ? `Parar ${site.domain}?` : `Reiniciar ${site.domain}?`}
        description={confirm === "stop" ? "O site ficará indisponível até ser iniciado novamente." : "O site poderá ficar temporariamente indisponível durante a reinicialização."}
        confirmLabel={confirm === "stop" ? "Parar Site" : "Reiniciar Site"}
        destructive={confirm === "stop"}
        onConfirm={() => { run(confirm); setConfirm(null); }}
      />
    </div>
  );
};

/* ---------- Serviço ---------- */
const ServiceCard = ({ svc }) => {
  const Icon = SERVICE_ICONS[svc.icon] || Globe;
  return (
    <div className="group rounded-2xl border border-line bg-surface p-5 transition-all duration-300 hover:-translate-y-1 hover:border-brand/25 hover:bg-surface-hover" data-testid={`portal-service-${svc.key}`}>
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Icon className="h-6 w-6" /></div>
      <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">{svc.title}</h3>
      <p className="mt-0.5 text-sm text-muted-foreground">{svc.subtitle}</p>
      <Button onClick={() => demoAction(svc.action)} variant="outline" className="mt-4 w-full border-line bg-transparent hover:border-brand/40 hover:bg-brand/5" data-testid={`portal-action-${svc.key}`}>
        {svc.action} <ExternalLink className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

/* ---------- Novo Chamado ---------- */
const NewTicketDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [form, setForm] = useState({subject:'',category:'Outro',priority:'medium',body:''});
  const [busy,setBusy] = useState(false);
  const submit = async () => {setBusy(true);try {await http.post('/tickets',{...form,client_id:clientId});onCreated();onOpenChange(false);setForm({subject:'',category:'Outro',priority:'medium',body:''});}catch(e){toast.error(e.message);}finally{setBusy(false);}};
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg border-line bg-popover">
      <DialogHeader>
        <DialogTitle className="font-heading">Novo Chamado</DialogTitle>
        <DialogDescription>Descreva sua solicitação. Nossa equipe responderá o mais breve possível.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="space-y-1.5"><Label>Assunto</Label><Input value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} placeholder="Resumo do problema" className="border-line bg-surface" data-testid="ticket-subject" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Categoria</Label>
            <Select value={form.category} onValueChange={category=>setForm({...form,category})}><SelectTrigger className="border-line bg-surface" data-testid="ticket-category"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent className="border-line bg-popover">{ticketCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Prioridade</Label>
            <Select value={form.priority} onValueChange={priority=>setForm({...form,priority})}><SelectTrigger className="border-line bg-surface" data-testid="ticket-priority"><SelectValue placeholder="Média" /></SelectTrigger>
              <SelectContent className="border-line bg-popover">{ticketPriorities.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        
        <div className="space-y-1.5"><Label>Descrição</Label><Textarea value={form.body} onChange={e=>setForm({...form,body:e.target.value})} rows={4} placeholder="Detalhe sua solicitação..." className="border-line bg-surface" data-testid="ticket-description" /></div>
        <button disabled title="Anexos ainda não implementados" className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-line py-3 text-sm text-muted-foreground hover:border-brand/40 hover:text-foreground" data-testid="ticket-attach"><Paperclip className="h-4 w-4" /> Anexar arquivo</button>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="border-line bg-transparent hover:bg-white/5">Cancelar</Button>
        <Button disabled={busy||!clientId||!form.subject||!form.body} onClick={submit} className="bg-brand text-white hover:bg-brand-hover" data-testid="ticket-submit">Abrir Chamado</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
 );
};

/* ---------- Detalhe do chamado (conversa) ---------- */
const TicketDialog = ({ ticket, onOpenChange, onReply }) => {
 const [body,setBody]=useState(''); const [busy,setBusy]=useState(false);
 const submit=async()=>{setBusy(true);try{await http.post(`/tickets/${ticket.id}/messages`,{body});setBody('');onReply();onOpenChange();}catch(e){toast.error(e.message);}finally{setBusy(false);}};
 return (
  <Dialog open={!!ticket} onOpenChange={(o) => !o && onOpenChange()}>
    <DialogContent className="max-w-lg border-line bg-popover">
      {ticket && (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{ticket.id}</span><StatusBadge status={ticket.status} size="sm" /></div>
            <DialogTitle className="font-heading">{ticket.subject}</DialogTitle>
            <DialogDescription>{ticket.category} · {ticket.service}</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-3 overflow-y-auto py-2">
            {ticket.messages.map((m, i) => (
              <div key={i} className={cn("flex", m.from === "client" ? "justify-end" : "justify-start")}>
                <div className={cn("max-w-[80%] rounded-2xl px-3 py-2 text-sm", m.from === "client" ? "bg-brand/15 text-foreground" : "border border-line bg-white/[0.02] text-foreground")}>
                  <p className="mb-0.5 text-[11px] text-muted-foreground">{m.author} · {m.at}</p>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-line pt-3">
            <Input value={body} onChange={e=>setBody(e.target.value)} placeholder="Escreva uma resposta..." className="border-line bg-surface" data-testid="ticket-reply-input" />
            <Button disabled={busy||!body.trim()} onClick={submit} className="bg-brand text-white hover:bg-brand-hover" data-testid="ticket-reply-send"><Send className="h-4 w-4" /></Button>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
};

export default function ClientPortal() {
  const {user,logout,hasPermission}=useAuth();
  const [version,setVersion]=useState(0);
  useEffect(()=>{const timer=setInterval(()=>setVersion(v=>v+1),20000);return()=>clearInterval(timer);},[]);
  const site = useAsync(() => http.get('/portal'),[version]);
  const services = {loading:false,error:null,data:[]};
  const ticketsQ = useAsync(() => api.getTickets(),[version]);
  const [newTicket, setNewTicket] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-line bg-background/80 px-4 backdrop-blur-xl sm:px-8">
        <TintiLogo />
        <div className="flex items-center gap-3">
          {hasPermission("servers.read") && <Link to="/" className="hidden items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground sm:inline-flex" data-testid="portal-back-admin"><ArrowLeft className="h-4 w-4" /> Voltar ao Admin</Link>}
          <Button variant="outline" onClick={logout}>Sair</Button>
          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5">
            <UserAvatar name={user?.name} size="sm" />
            <div className="hidden sm:block"><p className="text-xs font-semibold leading-tight">{user?.name}</p><p className="text-[11px] leading-tight text-muted-foreground">{user?.email}</p></div>
          </div>
        </div>
      </header>

      <main className="tc-grid-bg">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between animate-fade-up">
            <div>
              
              <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Olá, {user?.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">Gerencie seu site, serviços e suporte em um só lugar.</p>
            </div>
          </div>

          <Tabs defaultValue="projetos" className="animate-fade-up">
            <TabsList className="mb-6 inline-flex h-auto gap-1 border border-line bg-surface p-1">
              <TabsTrigger value="projetos" data-testid="portal-tab-projetos" className="data-[state=active]:bg-brand data-[state=active]:text-white">Meus Projetos</TabsTrigger>
              <TabsTrigger value="servicos" data-testid="portal-tab-servicos" className="data-[state=active]:bg-brand data-[state=active]:text-white">Sites</TabsTrigger>
              <TabsTrigger value="suporte" data-testid="portal-tab-suporte" className="data-[state=active]:bg-brand data-[state=active]:text-white"><LifeBuoy className="mr-1.5 h-4 w-4" /> Suporte</TabsTrigger>
            </TabsList>

            <TabsContent value="projetos"><PortalProjects /></TabsContent>

            <TabsContent value="servicos" className="space-y-8">
              <AsyncBoundary loading={site.loading} error={site.error} skeleton={<CardSkeleton />}>
                {site.data?.sites?.map(s=><SiteCard key={s.id} site={s}/>)}
                {site.data?.sites?.length===0 && <EmptyState title="Nenhum site vinculado" description="Seus sites aparecerão aqui após a configuração."/>}
              </AsyncBoundary>

              <div>
                <h2 className="mb-4 font-heading text-lg font-semibold text-foreground">Serviços</h2>
                <AsyncBoundary loading={services.loading} error={services.error} skeleton={<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>}>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {services.data?.map((s) => <ServiceCard key={s.key} svc={s} />)}
                    <div className="group flex flex-col items-start justify-center rounded-2xl border border-line bg-surface p-5 transition-all hover:-translate-y-1 hover:border-brand/25 hover:bg-surface-hover" data-testid="portal-service-support">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><LifeBuoy className="h-6 w-6" /></div>
                      <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">Suporte</h3>
                      <p className="mt-0.5 text-sm text-muted-foreground">Abra e acompanhe chamados</p>
                      <Button onClick={() => setNewTicket(true)} variant="outline" className="mt-4 w-full border-line bg-transparent hover:border-brand/40 hover:bg-brand/5" data-testid="portal-open-support"><Plus className="h-3.5 w-3.5" /> Novo Chamado</Button>
                    </div>
                  </div>
                </AsyncBoundary>
              </div>
            </TabsContent>

            <TabsContent value="suporte">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-foreground">Meus Chamados</h2>
                <Button onClick={() => setNewTicket(true)} className="bg-brand text-white hover:bg-brand-hover" data-testid="support-new"><Plus className="h-4 w-4" /> Novo Chamado</Button>
              </div>
              <AsyncBoundary loading={ticketsQ.loading} error={ticketsQ.error} skeleton={<CardSkeleton />}>
                {ticketsQ.data?.length ? (
                  <div className="space-y-3">
                    {ticketsQ.data.map((t) => (
                      <button key={t.id} onClick={() => setOpenTicket(t)} data-testid={`ticket-${t.id}`} className="flex w-full items-center gap-4 rounded-xl border border-line bg-surface p-4 text-left transition-colors hover:bg-surface-hover">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.03] text-muted-foreground"><MessageSquare className="h-5 w-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2"><span className="font-mono text-[11px] text-muted-foreground">{t.id}</span><span className={cn("text-[11px] font-medium", PRIORITY[t.priority])}>● {PRIORITY_LABEL[t.priority]}</span></div>
                          <p className="truncate text-sm font-medium text-foreground">{t.subject}</p>
                          <p className="truncate text-xs text-muted-foreground">{t.category} · atualizado {t.updated}</p>
                        </div>
                        <StatusBadge status={t.status} size="sm" className="shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={LifeBuoy} title="Nenhum chamado" description="Você ainda não abriu chamados." action={{ label: "Novo Chamado", onClick: () => setNewTicket(true) }} />
                )}
              </AsyncBoundary>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <NewTicketDialog open={newTicket} onOpenChange={setNewTicket} clientId={site.data?.client_id} onCreated={()=>setVersion(v=>v+1)} />
      <TicketDialog ticket={openTicket} onOpenChange={() => setOpenTicket(null)} onReply={()=>setVersion(v=>v+1)} />
    </div>
  );
}
